import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchDashboard, fetchMatrix,
  fetchEmployeeNames, fetchMatrixEmployeeNames,
} from "../../../API_Service/HappinessIndexSlice";
import {
  employeeID,
  companyID,
  DashboardCard,
  ChartSkeleton,
  InlineAlert,
} from "../../../Common/UIComponents";

const TABS  = ["Self", "Team", "Matrix"];
const MOODS = { 1: "Happy", 2: "Sad", 3: "Neutral" };
const IMG   = { 1: "/Images/Happy.png", 2: "/Images/Sad.png", 3: "/Images/Neutral.png" };

const MATRIX_COLOR = {
  GtoG: "#facc15", GtoY: "#fca5a5", GtoR: "#ef4444",
  YtoG: "#86efac", YtoY: "#facc15", YtoR: "#fca5a5",
  RtoG: "#22c55e", RtoY: "#86efac", RtoR: "#facc15",
};

/* ── Mood image with hover tooltip ── */
function MoodImg({ moodId, tooltipKey, date, session, companyId, dispatch, employeeNames }) {
  const [show, setShow] = useState(false);
  const names = employeeNames[tooltipKey] || [];

  const handleHover = () => {
    if (!employeeNames[tooltipKey]) {
      const [mood, sess] = tooltipKey.split("_");
      dispatch(fetchEmployeeNames({ mood, session: sess, companyId, date }));
    }
    setShow(true);
  };

  return (
    <div className="relative inline-block"
      onMouseEnter={handleHover} onMouseLeave={() => setShow(false)}>
      <img src={IMG[moodId]} alt={MOODS[moodId]} className="w-16 h-16" />
      {show && names.length > 0 && (
        <div className="absolute top-20 left-0 bg-white border border-slate-200 rounded-lg p-3 shadow-lg z-50 min-w-max max-h-48 overflow-y-auto">
          {names.map((n, i) => (
            <div key={i} className="text-xs py-1">
              <b className="text-slate-900">{n.EmployeeName}</b>
              {n.EnteredTime ? <span className="text-slate-500 ml-1">{n.EnteredTime}</span> : ""}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Team mood card row ── */
function TeamMoodRow({ happy, neutral, sad, total, average, date, session, companyId, dispatch, employeeNames }) {
  const hp = total > 0 ? Math.round(happy   * 100 / total) : 0;
  const np = total > 0 ? Math.round(neutral * 100 / total) : 0;
  const sp = total > 0 ? Math.round(sad     * 100 / total) : 0;

  return (
    <div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {[
          { moodId: 1, count: happy,   pct: hp, mood: "HAPPY", borderColor: "border-green-400", textColor: "text-green-600", barColor: "bg-green-500"   },
          { moodId: 3, count: neutral, pct: np, mood: "NEUTRAL", borderColor: "border-yellow-400", textColor: "text-yellow-600", barColor: "bg-yellow-500" },
          { moodId: 2, count: sad,     pct: sp, mood: "SAD", borderColor: "border-red-400", textColor: "text-red-600", barColor: "bg-red-500"     },
        ].map(({ moodId, count, pct, mood, borderColor, textColor, barColor }) => (
          <div key={mood} className={`bg-white rounded-lg border ${borderColor} p-3 flex flex-col items-center text-center hover:shadow-sm transition-shadow duration-200`}>
            <MoodImg
              moodId={moodId}
              tooltipKey={`${mood}_${session}`}
              date={date} session={session}
              companyId={companyId}
              dispatch={dispatch} employeeNames={employeeNames}
            />
            <div className={`font-bold text-sm ${textColor} mt-2`}>{MOODS[moodId]}</div>
            <div className="w-full bg-slate-200 rounded-full h-1.5 mt-2 mb-1 overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${barColor}`}
                style={{ width: `${pct}%` }}
              ></div>
            </div>
            <div className="text-xs text-slate-600">
              <span className={`font-bold ${textColor}`}>{pct}%</span> <span className="text-slate-500">({count})</span>
            </div>
          </div>
        ))}
        <div className="bg-white rounded-lg border border-[#08757C]/40 p-3 flex flex-col items-center text-center hover:shadow-sm transition-shadow duration-200">
          {average && average !== "NA"
            ? <img src={IMG[average === "Happy" ? 1 : average === "Sad" ? 2 : 3]}
                alt={average} className="w-10 h-10" />
            : <div className="text-lg">—</div>
          }
          <div className="font-bold text-sm text-slate-900 mt-2">Overall</div>
          <div className="text-xs font-bold text-[#08757C] mt-1">{!average || average === "NA" ? "No data" : average}</div>
        </div>
      </div>
    </div>
  );
}

/* ── Matrix cell ── */
function MatrixCell({ value, colorKey, startMood, endMood, date, companyId, dispatch, employeeNames }) {
  const [show, setShow] = useState(false);
  const key   = `${startMood}_${endMood}`;
  const names = employeeNames[key] || [];
  const bg    = value > 0 ? MATRIX_COLOR[colorKey] : "";

  const handleHover = () => {
    if (value > 0 && !employeeNames[key])
      dispatch(fetchMatrixEmployeeNames({ startMood, endMood, companyId, date }));
    if (value > 0) setShow(true);
  };

  return (
    <td
      className="px-4 py-3 text-center font-semibold text-sm border border-slate-200 cursor-default relative hover:opacity-80 transition-opacity"
      style={{ backgroundColor: bg }}
      onMouseEnter={handleHover}
      onMouseLeave={() => setShow(false)}
    >
      {value}
      {show && names.length > 0 && (
        <div className="absolute top-12 left-0 bg-white border border-slate-200 rounded-lg p-3 shadow-lg z-20 min-w-max max-h-48 overflow-y-auto">
          {names.map((n, i) => (
            <div key={i} className="text-xs py-1 whitespace-nowrap">
              <b className="text-slate-900">{n.EmployeeName}</b>
              {n.EnteredTime ? <span className="text-slate-500 ml-1">Start: {n.EnteredTime}</span> : ""}
              {n.ENDEntered_Time ? <span className="text-slate-500 ml-1">End: {n.ENDEntered_Time}</span> : ""}
            </div>
          ))}
        </div>
      )}
    </td>
  );
}

/* ── Main Component ── */
export default function HappinessIndexDashboard() {
  const dispatch = useDispatch();
  const user     = useSelector((s) => s.auth.user);
  const { dashboard, dashboardError, matrix, employeeNames } =
    useSelector((s) => s.happinessIndex);

  const [activeTab,    setActiveTab]    = useState("Team");
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split("T")[0]);

  const load = (date) => {
   
    dispatch(fetchDashboard({ employeeId: employeeID, companyId: companyID, date }));
    dispatch(fetchMatrix({ companyId: companyID, date }));
  };

 useEffect(() => {
    if (!employeeID || !companyID) return;

    load(selectedDate);
}, [employeeID, companyID]);

  const handleDateChange = (e) => {
    setSelectedDate(e.target.value);
    load(e.target.value);
  };

  // Loading state
  if (!dashboard && !dashboardError)
    return (
      <DashboardCard icon="sentiment_satisfied" title="Happiness Index">
        <ChartSkeleton height={160} />
      </DashboardCard>
    );

  // Error state
  if (dashboardError)
    return (
      <DashboardCard icon="sentiment_satisfied" title="Happiness Index">
        <InlineAlert type="error" message="Failed to load dashboard data." />
      </DashboardCard>
    );

  return (
    <DashboardCard
      icon="sentiment_satisfied"
      title="Happiness Index"
      subtitle="Track team and personal mood trends"
      actions={
        <input
          type="date"
          value={selectedDate}
          onChange={handleDateChange}
          className="px-3 py-1.5 border border-slate-200 rounded-md text-xs font-medium text-slate-700 bg-white hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-[#08757C]/50 transition-all"
        />
      }
    >
      {/* Tabs */}
      <div className="flex gap-2 mb-6 bg-slate-50 rounded-lg p-1 w-fit">
        {TABS.map((t) => (
          <button
            key={t}
            onClick={() => setActiveTab(t)}
            className={`px-6 py-2 text-sm font-semibold rounded-md transition-all ${
              activeTab === t
                ? "bg-white text-[#08757C] shadow-md"
                : "text-slate-600 hover:text-slate-900"
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      {/* ── SELF TAB ── */}
      {activeTab === "Self" && (
        <div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              { label: "Beginning of Day",  moodId: dashboard.MoodID,    time: dashboard.EnteredTime, icon: "🌅"     },
              { label: "End of Day", moodId: dashboard.ENDMoodID, time: dashboard.ENDEntered_Time, icon: "🌆" },
            ].map(({ label, moodId, time, icon }) => (
              <div
                key={label}
                className="relative bg-white rounded-lg border border-slate-200 p-4 flex flex-col items-center text-center hover:shadow-md hover:border-[#08757C]/50 transition-all duration-200 overflow-hidden"
              >
                <div className="absolute top-3 right-3 text-2xl">{icon}</div>
                <p className="text-sm font-semibold text-slate-700 mb-3">{label}</p>
                {moodId > 0 ? (
                  <>
                    <img src={IMG[moodId]} alt="" className="w-16 h-16 mb-3" />
                    <p className="text-base font-bold text-slate-900">{MOODS[moodId]}</p>
                    {time && <p className="text-xs text-slate-500 mt-2 font-medium">{time}</p>}
                  </>
                ) : (
                  <div className="flex flex-col items-center gap-2">
                    <span className="text-3xl">❌</span>
                    <p className="text-xs text-slate-400 font-medium">Not yet logged</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── TEAM TAB ── */}
      {activeTab === "Team" && (
        <div className="space-y-5">
          <div>
            <p className="text-sm font-semibold text-slate-700 mb-3">Beginning Team Mood</p>
            <TeamMoodRow
              label=""
              happy={dashboard.HappyCount}
              neutral={dashboard.NeutralCount}
              sad={dashboard.SadCount}
              total={dashboard.TotalCount}
              average={dashboard.AverageHappyMood}
              date={selectedDate}
              session="Begining"
              companyId={companyID}
              dispatch={dispatch}
              employeeNames={employeeNames}
            />
          </div>
          <div className="pt-4 border-t border-slate-200">
            <p className="text-sm font-semibold text-slate-700 mb-3">End of Day Team Mood</p>
            <TeamMoodRow
              label=""
              happy={dashboard.ENDHappyCount}
              neutral={dashboard.ENDNeutralCount}
              sad={dashboard.ENDSadCount}
              total={dashboard.ENDTotalCount}
              average={dashboard.ENDAverageHappyMood}
              date={selectedDate}
              session="End"
              companyId={companyID}
              dispatch={dispatch}
              employeeNames={employeeNames}
            />
          </div>
        </div>
      )}

      {/* ── MATRIX TAB ── */}
      {activeTab === "Matrix" && (
        <div className="space-y-6">
          <div className="flex items-center gap-2 mb-2">
            <span className="inline-block w-3 h-3 rounded-full bg-[#08757C]"></span>
            <p className="text-sm font-semibold text-slate-700">Mood Transition Matrix</p>
          </div>
          {matrix && matrix.matrixModel ? (
            <div className="space-y-8">
              <div className="overflow-x-auto rounded-lg border border-slate-200">
                <table className="w-full border-collapse min-w-full">
                  <thead>
                    <tr>
                      <th colSpan={5} className="bg-[#08757C] px-4 py-3 text-center font-bold text-white border border-slate-200">
                        Beginning → End of Day
                      </th>
                    </tr>
                    <tr>
                      <th className="bg-slate-50 px-4 py-3 text-center font-semibold text-sm text-slate-900 border border-slate-200"></th>
                      <th className="bg-slate-50 px-4 py-3 text-center font-semibold text-sm text-slate-900 border border-slate-200"></th>
                      <th className="bg-green-50 px-4 py-3 text-center font-semibold text-sm text-green-900 border border-green-200">😊 Happy</th>
                      <th className="bg-yellow-50 px-4 py-3 text-center font-semibold text-sm text-yellow-900 border border-yellow-200">😐 Neutral</th>
                      <th className="bg-red-50 px-4 py-3 text-center font-semibold text-sm text-red-900 border border-red-200">😢 Sad</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td rowSpan={3} className="bg-slate-50 px-4 py-3 text-center font-bold text-slate-900 border border-slate-200">
                        Beginning
                      </td>
                      <td className="bg-slate-50 px-4 py-3 text-center font-semibold text-slate-700 border border-slate-200">
                        😊 Happy
                      </td>
                      <MatrixCell value={matrix.matrixModel.GtoG} colorKey="GtoG" startMood={1} endMood={1} date={selectedDate} companyId={companyID} dispatch={dispatch} employeeNames={employeeNames} />
                      <MatrixCell value={matrix.matrixModel.GtoY} colorKey="GtoY" startMood={1} endMood={3} date={selectedDate} companyId={companyID} dispatch={dispatch} employeeNames={employeeNames} />
                      <MatrixCell value={matrix.matrixModel.GtoR} colorKey="GtoR" startMood={1} endMood={2} date={selectedDate} companyId={companyID} dispatch={dispatch} employeeNames={employeeNames} />
                    </tr>
                    <tr>
                      <td className="bg-slate-50 px-4 py-3 text-center font-semibold text-slate-700 border border-slate-200">
                        😐 Neutral
                      </td>
                      <MatrixCell value={matrix.matrixModel.YtoG} colorKey="YtoG" startMood={3} endMood={1} date={selectedDate} companyId={companyID} dispatch={dispatch} employeeNames={employeeNames} />
                      <MatrixCell value={matrix.matrixModel.YtoY} colorKey="YtoY" startMood={3} endMood={3} date={selectedDate} companyId={companyID} dispatch={dispatch} employeeNames={employeeNames} />
                      <MatrixCell value={matrix.matrixModel.YtoR} colorKey="YtoR" startMood={3} endMood={2} date={selectedDate} companyId={companyID} dispatch={dispatch} employeeNames={employeeNames} />
                    </tr>
                    <tr>
                      <td className="bg-slate-50 px-4 py-3 text-center font-semibold text-slate-700 border border-slate-200">
                        😢 Sad
                      </td>
                      <MatrixCell value={matrix.matrixModel.RtoG} colorKey="RtoG" startMood={2} endMood={1} date={selectedDate} companyId={companyID} dispatch={dispatch} employeeNames={employeeNames} />
                      <MatrixCell value={matrix.matrixModel.RtoY} colorKey="RtoY" startMood={2} endMood={3} date={selectedDate} companyId={companyID} dispatch={dispatch} employeeNames={employeeNames} />
                      <MatrixCell value={matrix.matrixModel.RtoR} colorKey="RtoR" startMood={2} endMood={2} date={selectedDate} companyId={companyID} dispatch={dispatch} employeeNames={employeeNames} />
                    </tr>
                  </tbody>
                </table>
              </div>
              
              {/* Yearly Statistics */}
              <div className="overflow-x-auto rounded-lg border border-slate-200">
                <table className="w-full border-collapse min-w-full">
                  <thead>
                    <tr>
                      <th colSpan={4} className="bg-slate-50 px-4 py-3 text-center font-bold text-slate-900 border border-slate-200">
                        Yearly Overview ({new Date(selectedDate).getFullYear()})
                      </th>
                    </tr>
                    <tr>
                      <th className="bg-slate-50 px-4 py-3 text-center font-semibold text-sm text-slate-700 border border-slate-200"></th>
                      <th className="bg-green-50 px-4 py-3 text-center font-semibold text-sm text-green-900 border border-slate-200">😊 Happy</th>
                      <th className="bg-yellow-50 px-4 py-3 text-center font-semibold text-sm text-yellow-900 border border-slate-200">😐 Neutral</th>
                      <th className="bg-red-50 px-4 py-3 text-center font-semibold text-sm text-red-900 border border-slate-200">😢 Sad</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="bg-slate-50 px-4 py-3 text-center font-semibold text-slate-700 border border-slate-200">Beginning</td>
                      <td className="px-4 py-3 text-center font-bold text-slate-900 border border-slate-200">
                        {matrix.YearlyTotalCount > 0 ? Math.round((matrix.YearlyHappyCount * 100) / matrix.YearlyTotalCount) : 0}%
                      </td>
                      <td className="px-4 py-3 text-center font-bold text-slate-900 border border-slate-200">
                        {matrix.YearlyTotalCount > 0 ? Math.round((matrix.YearlyNeutralCount * 100) / matrix.YearlyTotalCount) : 0}%
                      </td>
                      <td className="px-4 py-3 text-center font-bold text-slate-900 border border-slate-200">
                        {matrix.YearlyTotalCount > 0 ? Math.round((matrix.YearlySadCount * 100) / matrix.YearlyTotalCount) : 0}%
                      </td>
                    </tr>
                    <tr>
                      <td className="bg-slate-50 px-4 py-3 text-center font-semibold text-slate-700 border border-slate-200">End</td>
                      <td className="px-4 py-3 text-center font-bold text-slate-900 border border-slate-200">
                        {matrix.ENDYearlyTotalCount > 0 ? Math.round((matrix.ENDYearlyHappyCount * 100) / matrix.ENDYearlyTotalCount) : 0}%
                      </td>
                      <td className="px-4 py-3 text-center font-bold text-slate-900 border border-slate-200">
                        {matrix.ENDYearlyTotalCount > 0 ? Math.round((matrix.ENDYearlyNeutralCount * 100) / matrix.ENDYearlyTotalCount) : 0}%
                      </td>
                      <td className="px-4 py-3 text-center font-bold text-slate-900 border border-slate-200">
                        {matrix.ENDYearlyTotalCount > 0 ? Math.round((matrix.ENDYearlySadCount * 100) / matrix.ENDYearlyTotalCount) : 0}%
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <p className="text-slate-500 text-center py-8">No matrix data for this date.</p>
          )}
        </div>
      )}
    </DashboardCard>
  );
}